// ABOUTME: News management view with article listing and create button.
// ABOUTME: Articles are fetched from root-api; clicking a draft reopens the builder, clicking a published article opens the viewer.

import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../../../theme";
import Text from "../../../../components/theme/text/Text";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import ShLabel from "../../../../components/theme/label/ShLabel";
import MCreateNewsArticle from "../../../../components/global/Modals/MCreateNewsArticle/MCreateNewsArticle";
import MViewNewsArticle from "../../../../components/global/Modals/MCreateNewsArticle/MViewNewsArticle";
import {
    useGetNewsArticlesQuery,
    usePublishNewsArticleMutation,
} from "../../../../newapi/global/newsRootSlice";

const AUDIENCE_SCOPE_ALL = "all";

// Audience values as persisted on article.audience by root-api, in the order they
// appear in the builder. "customers" covers fulfilment companies; "webshops" covers
// customers that only fulfil their own orders.
const AUDIENCE_VALUES = ["customers", "webshops", "relations"];

// Each audience gets its own colour so a card is identifiable at a glance rather
// than by reading the label. Blue, orange and purple sit far apart on the wheel;
// green and red are deliberately unused, they already carry status meaning here.
const audienceLabelVariant = (audienceValue, theme) => {
    if (audienceValue === "webshops") return "orange";
    if (audienceValue === "relations") return "purple";
    if (theme.palette.mode === "dark") return "blue";
    return "primary";
};

const AudienceScopeToggle = ({ scope, setScope, counts, total }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(_, v) => v && setScope(v)}
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                borderRadius: 5,
                p: 0.5,
                gap: 0.5,
                height: 40,
                width: "fit-content",
                "& .MuiToggleButtonGroup-grouped": {
                    border: 0,
                    borderRadius: 4,
                    whiteSpace: "nowrap",
                    textTransform: 'none',
                    fontSize: 14,
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    px: 2,
                    color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                    "&.Mui-selected": {
                        fontWeight: 600,
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? "none" : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? "none" : `0px 2px 4px ${colors.grey[200]}`,
                        color: colors.txt["primary"],
                    },
                    "&:hover": {
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                    },
                },
            }}
        >
            <ToggleButton value={AUDIENCE_SCOPE_ALL}>{`${t("all")} (${total})`}</ToggleButton>
            {AUDIENCE_VALUES.map((audienceValue) => (
                <ToggleButton key={audienceValue} value={audienceValue}>
                    {`${t(audienceValue)} (${counts[audienceValue] ?? 0})`}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};

const formatDate = (raw) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};

const All = ({ settings = [], ...props }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedArticleId, setSelectedArticleId] = useState(null);

    const [audienceScope, setAudienceScope] = useState(AUDIENCE_SCOPE_ALL);

    const { data: articles = [] } = useGetNewsArticlesQuery({ articleType: "news" });
    const [publishArticle] = usePublishNewsArticleMutation();

    const audienceCounts = useMemo(() => {
        const counts = {};
        for (const article of articles) {
            for (const audienceValue of (article.audience ?? [])) {
                counts[audienceValue] = (counts[audienceValue] ?? 0) + 1;
            }
        }
        return counts;
    }, [articles]);

    let visibleArticles = articles;
    if (audienceScope !== AUDIENCE_SCOPE_ALL) {
        visibleArticles = articles.filter((a) => (a.audience ?? []).includes(audienceScope));
    }

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });

        return () => {
        }
    }, []);

    const selectedArticle = articles.find((a) => a.id === selectedArticleId) ?? null;

    const handleCardClick = (article) => {
        setSelectedArticleId(article.id);
        if (article.status === "published") {
            setViewModalOpen(true);
        } else {
            setCreateModalOpen(true);
        }
    };

    const handleCreateClose = () => {
        setSelectedArticleId(null);
        setCreateModalOpen(false);
    };

    const handleViewClose = () => {
        setSelectedArticleId(null);
        setViewModalOpen(false);
    };

    const handlePublishFromViewer = async (article) => {
        try {
            await publishArticle(article.id).unwrap();
            handleViewClose();
        } catch (err) {
            console.error("[news] publish failed", err);
        }
    };

    return (
        <Box
            sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <AudienceScopeToggle
                    scope={audienceScope}
                    setScope={setAudienceScope}
                    counts={audienceCounts}
                    total={articles.length}
                />
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        onClick={() => { setSelectedArticleId(null); setCreateModalOpen(true); }}
                        endIcon={<AddIcon />}
                        variant="contained"
                    >
                        {t("create_article") || "Artikel aanmaken"}
                    </Shbutton>
                </ButtonGroup>
            </Box>

            {/* Article list */}
            {visibleArticles.length === 0 ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                    <Text light>{t("no_articles_yet") || "Nog geen artikelen"}</Text>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 2,
                    }}
                >
                    {visibleArticles.map((article) => {
                        const isPublished = article.status === "published";
                        const audiences = article.audience ?? [];
                        return (
                            <Box
                                key={article.id}
                                onClick={() => handleCardClick(article)}
                                sx={{
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    background: theme.palette.mode === "dark" ? colors.grey[300] : "#fff",
                                    boxShadow: `0px 1px 2px 1px rgba(49, 65, 95, 0.15)`,
                                    transition: "150ms",
                                    "&:hover": {
                                        boxShadow: `0px 4px 12px rgba(49, 65, 95, 0.2)`,
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                {/* Card image */}
                                {(article.headerImage?.url || article.headerImage?.previewUrl) ? (
                                    <Box
                                        component="img"
                                        src={article.headerImage.url || article.headerImage.previewUrl}
                                        alt=""
                                        sx={{
                                            width: "100%",
                                            height: 140,
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 140,
                                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Text light>{t("no_image") || "Geen afbeelding"}</Text>
                                    </Box>
                                )}

                                {/* Card body */}
                                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",mb:1, }}>
                                        <Box
                                            sx={{
                                                px: 1,
                                                borderRadius: 3,
                                                height:24,
                                                lineHeight:"24px",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                fontFamily: "Roboto, sans-serif",
                                                background: isPublished
                                                    ? colors.green[100]
                                                    : theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                                color: isPublished
                                                    ? colors.green[400]
                                                    : colors.txt["secondary"],
                                            }}
                                        >
                                            {isPublished ? t("published") || "Gepubliceerd" : t("draft") || "Concept"}
                                        </Box>
                                        {audiences.map((audienceValue) => (
                                            <ShLabel
                                                key={audienceValue}
                                                size={24}
                                                fitted
                                                palette="normal"
                                                variant={audienceLabelVariant(audienceValue, theme)}
                                            >
                                                {t(audienceValue)}
                                            </ShLabel>
                                        ))}
                                    </Box>
                                    <Text bold fontSize={15}>
                                        {article.title || t("untitled") || "Zonder titel"}
                                    </Text>
                                    <Text light fontSize={12}>
                                        {formatDate(article.publishedAt || article.createdAt)}
                                    </Text>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <MCreateNewsArticle
                open={createModalOpen}
                handleClose={handleCreateClose}
                articleId={selectedArticleId}
            />

            <MViewNewsArticle
                open={viewModalOpen}
                handleClose={handleViewClose}
                article={selectedArticle}
                onPublish={handlePublishFromViewer}
            />
        </Box>
    );
};

export default All;
